import { useEffect } from 'react';
import { isPosSizingActualRecoveryAvailable } from '../dashboardUtils';

export default function usePosSizingFreshnessPrompt({
    userUid,
    actualPortfolioIsReady,
    activeView,
    posSizingSyncStatus,
    posSizingSyncStatusLoading,
    posSizingLoading,
    priceRefreshLoading,
    pendingPosSizingAutoSync,
    contextUpdateState,
    autoSyncPreferencesReady = true,
    posSizingAutoSyncEnabled = false,
    setError,
    setPosSizingFreshnessDetailsOpen,
    posSizingEntrySequenceRef,
    posSizingFreshnessPromptStateRef,
}) {
    useEffect(() => {
        if (activeView !== 'pos-sizing') return;
        if (!posSizingSyncStatus?.requires_snapshot_sync) return;
        setError(null);
    }, [activeView, posSizingSyncStatus?.requires_snapshot_sync, setError]);

    useEffect(() => {
        if (!userUid) return;
        if (activeView !== 'pos-sizing') return;

        const currentEntrySequence = posSizingEntrySequenceRef.current;
        if (!autoSyncPreferencesReady || posSizingAutoSyncEnabled) return;
        if (posSizingSyncStatusLoading || posSizingLoading || priceRefreshLoading) return;
        if (pendingPosSizingAutoSync) return;
        if (!posSizingSyncStatus) return;

        const requiresActualRecovery = isPosSizingActualRecoveryAvailable(
            posSizingSyncStatus,
            actualPortfolioIsReady,
        );
        const durableContextIsStale = (
            posSizingSyncStatus?.optimizer_context?.freshness === 'stale'
        );
        const actionable = Boolean(
            posSizingSyncStatus?.is_stale
            || posSizingSyncStatus?.requires_snapshot_sync
            || posSizingSyncStatus?.requires_state_hash_sync
            || posSizingSyncStatus?.requires_actual_sync
            || durableContextIsStale
            || requiresActualRecovery
        );
        const priorPromptState = posSizingFreshnessPromptStateRef.current;
        const wasActionable = Boolean(
            priorPromptState?.entrySequence === currentEntrySequence
            && priorPromptState?.actionable
        );
        if (!posSizingSyncStatus?.has_ideas && !requiresActualRecovery) {
            if (actualPortfolioIsReady == null) return;
            posSizingFreshnessPromptStateRef.current = {
                entrySequence: currentEntrySequence,
                actionable: false,
            };
            return;
        }
        if (!actionable) {
            posSizingFreshnessPromptStateRef.current = {
                entrySequence: currentEntrySequence,
                actionable: false,
            };
            return;
        }
        if (contextUpdateState && contextUpdateState.phase !== 'awaiting_acceptance') return;
        if (wasActionable) return;

        posSizingFreshnessPromptStateRef.current = {
            entrySequence: currentEntrySequence,
            actionable: true,
        };
        setPosSizingFreshnessDetailsOpen(true);
    }, [
        activeView,
        autoSyncPreferencesReady,
        contextUpdateState,
        posSizingAutoSyncEnabled,
        actualPortfolioIsReady,
        pendingPosSizingAutoSync,
        posSizingEntrySequenceRef,
        posSizingFreshnessPromptStateRef,
        posSizingLoading,
        posSizingSyncStatus,
        posSizingSyncStatus?.has_ideas,
        posSizingSyncStatus?.is_stale,
        posSizingSyncStatus?.optimizer_context?.freshness,
        posSizingSyncStatus?.requires_snapshot_sync,
        posSizingSyncStatusLoading,
        priceRefreshLoading,
        setPosSizingFreshnessDetailsOpen,
        userUid,
    ]);
}
